<script setup>
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue';
import { useRouter } from 'vue-router';
import { useDomainsStore } from '#modules/domains/stores/domains.store.js';
import { useInfiniteQuery } from '@tanstack/vue-query';
import { vInfiniteScroll } from '@vueuse/components';
import { computed } from 'vue';
import CFCircleSpinner from '@/shared/components/CFCircleSpinner.vue';
import CFIcon from '@/ui/Icon/CFIcon.vue';

const props = defineProps({
    current: {
        type: Object,
        default: null,
    },
});

const router = useRouter();
const domainStore = useDomainsStore();

const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isError } = useInfiniteQuery({
    queryKey: ['domains-infinite'],
    queryFn: async ({ pageParam = 1 }) => {
        const { data } = await domainStore.getDomains({ page: pageParam, sort: 'name' });

        const currentPage = data.meta.current_page;
        const lastPage = data.meta.last_page;

        return {
            data: data.data,
            cursor: currentPage < lastPage ? currentPage + 1 : undefined,
        };
    },
    getNextPageParam: (lastPage) => lastPage.cursor,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
});

const filteredPages = computed(() => {
    if (!data.value?.pages) {
        return [];
    }

    return data.value.pages.map((page) => ({
        ...page,
        data: page.data.filter((domain) => domain.id !== props.current?.id),
    }));
});

const hasFilteredDomains = computed(() => {
    return filteredPages.value.some((page) => page.data.length > 0);
});

const changeDomain = async (domain) => {
    if (domain.id === props.current?.id) {
        return;
    }

    await router.push({ name: 'single domain', params: { domain: domain.id } });
};

const onLoadMore = () => {
    if (!hasNextPage.value || isFetchingNextPage.value) {
        return;
    }

    fetchNextPage();
};

const canLoadMore = () => hasNextPage.value && !isFetchingNextPage.value;
</script>

<template>
    <Listbox
        :model-value="current"
        @update:model-value="changeDomain"
    >
        <div class="relative">
            <ListboxButton
                class="relative flex h-15 w-full cursor-pointer items-center justify-between gap-2 border-b border-b-black text-left"
            >
                <template v-if="current">
                    <span
                        v-show="current.attributes.name"
                        class="block truncate text-xl leading-6 font-bold"
                    >
                        {{ current.attributes.name }}.{{ current.attributes.tld }}
                    </span>
                </template>
                <CFCircleSpinner
                    v-else
                    class="size-5"
                />
                <span class="pointer-events-none flex items-center">
                    <CFIcon
                        name="arrow-down1"
                        class="size-4 text-black"
                        aria-hidden="true"
                    />
                </span>
            </ListboxButton>

            <Transition
                leave-active-class="transition duration-100 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <ListboxOptions
                    v-infinite-scroll="[onLoadMore, { distance: 20, canLoadMore }]"
                    class="bg-grey-light absolute max-h-[calc(100vh-200px)] w-full overflow-x-hidden overflow-y-auto"
                >
                    <div class="relative">
                        <div
                            v-if="isPending"
                            class="flex items-center justify-center py-10"
                            role="status"
                        >
                            <CFCircleSpinner class="size-6" />
                            <span class="sr-only">{{ $t('general.loading') }}...</span>
                        </div>

                        <div
                            v-else-if="isError"
                            class="px-5 py-7 text-center text-sm text-red-600"
                        >
                            {{ $t('errors.component_load_failed') }}
                        </div>

                        <template v-else>
                            <template
                                v-for="(group, index) in filteredPages"
                                :key="index"
                            >
                                <ListboxOption
                                    v-for="option in group.data"
                                    :key="option.id"
                                    :value="option"
                                    as="template"
                                >
                                    <li
                                        class="relative flex cursor-pointer flex-col border-b border-b-black py-5 select-none"
                                    >
                                        <span class="w-fit pr-2.5 text-xl font-bold">
                                            {{ option.attributes.name }}.{{ option.attributes.tld }}
                                        </span>
                                    </li>
                                </ListboxOption>
                            </template>

                            <p
                                v-if="!hasFilteredDomains && !isFetchingNextPage"
                                class="px-5 py-7 text-sm select-none"
                            >
                                {{ $t('domain.no_results') }}
                            </p>

                            <div
                                v-if="isFetchingNextPage"
                                class="bg-grey-light absolute right-0 bottom-0 left-0 py-6"
                                role="status"
                            >
                                <CFCircleSpinner class="mx-auto size-6" />
                                <span class="sr-only">{{ $t('general.loading') }}...</span>
                            </div>
                        </template>
                    </div>
                </ListboxOptions>
            </Transition>
        </div>
    </Listbox>
</template>
